import { useState } from 'react';
import {
  FiltersContainer,
  MainStats,
  SideStatBox,
  SideStats,
  StatBox,
  StatisticsWrapper,
} from '../../../pages/Dashboard/styles';
import FilterDateDashboard from '../FilterDateDashboard';
import { Skeleton, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { IDashboardSolicitationStatsViewModel } from 'viewModels';
import { useQuery } from 'react-query';
import { SolicitationController } from '../../../controller/SolicitationController';
import { 
  requestDashboardSolicitationsReport,
  exportSolicitationsByCreator,
  exportCompletedSolicitationsByCorrespondent,
} from '../../../controller/ReportController';
import { useAlert } from '../../../hooks/useAlert';
import { BasicButton } from '../../Buttons/BasicButton';

export default function TabSolicitationDashboard() {
  const alert = useAlert();
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'days'));
  const [endDate, setEndDate] = useState(dayjs());
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingByCreator, setIsExportingByCreator] = useState(false);
  const [isExportingByCorrespondent, setIsExportingByCorrespondent] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<IDashboardSolicitationStatsViewModel>({
    totalOpened: 0,
    totalInProgress: 0,
    totalConcluded: 0,
  });

  const { isFetching, isLoading } = useQuery(
    ['dashboardSolicitationStats', startDate, endDate],
    () =>
      SolicitationController.getDashboardSolicitationStats(startDate.toDate(), endDate.toDate()),
    {
      onSuccess: (response) => {
        setDashboardStats(response);
      },
      enabled: !!startDate && !!endDate,
      refetchOnWindowFocus: false,
      retryDelay: 10000,
    },
  );

  const handleExportReport = async (start: Date, end: Date) => {
    if (isExporting) return;
    setIsExporting(true);
    await requestDashboardSolicitationsReport(start, end)
      .then(() => {
        alert.show({ message: 'Relatório solicitado com sucesso. Você receberá uma notificação quando estiver pronto.', type: 'success' });
      })
      .catch(() => {
        alert.show({ message: 'Erro ao solicitar relatório', type: 'error' });
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  const handleExportByCreator = async () => {
    if (isExportingByCreator || !startDate || !endDate) return;
    setIsExportingByCreator(true);
    await exportSolicitationsByCreator(startDate.toDate(), endDate.toDate())
      .then(() => {
        alert.show({ message: 'Relatório exportado com sucesso!', type: 'success' });
      })
      .catch(() => {
        alert.show({ message: 'Erro ao exportar relatório', type: 'error' });
      })
      .finally(() => {
        setIsExportingByCreator(false);
      });
  };

  const handleExportByCorrespondent = async () => {
    if (isExportingByCorrespondent || !startDate || !endDate) return;
    setIsExportingByCorrespondent(true);
    await exportCompletedSolicitationsByCorrespondent(startDate.toDate(), endDate.toDate())
      .then(() => {
        alert.show({ message: 'Relatório exportado com sucesso!', type: 'success' });
      })
      .catch(() => {
        alert.show({ message: 'Erro ao exportar relatório', type: 'error' });
      })
      .finally(() => {
        setIsExportingByCorrespondent(false);
      });
  };

  return (
    <div>
      <FilterDateDashboard
        defaultValue={[startDate, endDate]}
        isExporting={isExporting}
        onClickExport={(start, end) => handleExportReport(start, end)}
        disabled={isLoading || isFetching || isExporting}
        changeDate={(dates) => {
          if (Array.isArray(dates)) {
            const [start, end] = dates;
            if (start && end) {
              setStartDate(start);
              setEndDate(end);
            }
          }
        }}
      />
      
      <FiltersContainer style={{ marginTop: '16px' }}>
        <BasicButton
          disabled={isLoading || isFetching || isExportingByCreator || !startDate || !endDate}
          variant="contained"
          size="large"
          onClick={handleExportByCreator}>
          {isExportingByCreator ? (
            <div className="button-text">
              Extraindo <CircularProgress style={{ color: 'white' }} size="15px" />
            </div>
          ) : (
            <span style={{ whiteSpace: 'nowrap' }}>Por Criador</span>
          )}
        </BasicButton>
        
        <BasicButton
          disabled={isLoading || isFetching || isExportingByCorrespondent || !startDate || !endDate}
          variant="contained"
          size="large"
          style={{ width: 'auto', minWidth: '280px' }}
          onClick={handleExportByCorrespondent}>
          {isExportingByCorrespondent ? (
            <div className="button-text">
              Extraindo <CircularProgress style={{ color: 'white' }} size="15px" />
            </div>
          ) : (
            <span style={{ whiteSpace: 'nowrap' }}>Concluídas por Correspondente</span>
          )}
        </BasicButton>
      </FiltersContainer>

      <StatisticsWrapper>
        <MainStats>
          <StatBox>
            <span>Solicitações Abertas</span>
            {isLoading || isFetching ? (
              <Skeleton variant="text" width={80} />
            ) : (
              <span>{dashboardStats.totalOpened}</span>
            )}
          </StatBox>
        </MainStats>

        <SideStats>
          <SideStatBox>
            <span className="date">
              {`De ${startDate.format('DD/MM/YYYY')} a ${endDate.format('DD/MM/YYYY')}`}
            </span>
            {isLoading || isFetching ? (
              <Skeleton variant="text" width={80} />
            ) : (
              <span>{dashboardStats.totalInProgress}</span>
            )}
            <span>Solicitações Em Andamento</span>
          </SideStatBox>
          <SideStatBox className="inactive">
            <span className="date">
              {`De ${startDate.format('DD/MM/YYYY')} a ${endDate.format('DD/MM/YYYY')}`}
            </span>
            {isLoading || isFetching ? (
              <Skeleton variant="text" width={80} />
            ) : (
              <span>{dashboardStats.totalConcluded}</span>
            )}
            <span>Solicitações Concluídas</span>
          </SideStatBox>
        </SideStats>
      </StatisticsWrapper>
    </div>
  );
}
